/**
 * Apply CTA — click loader
 *
 * Surfaces a spinner on the "Apply now" button while the browser navigates
 * to `/apply/?…`. The Feenstra handover hop can take a couple of seconds
 * (DB lookup → API roundtrip → 302 → fg-nexus.co.za), and without visible
 * feedback users tend to double-click or assume the page is broken.
 *
 * Behaviour:
 *   - Click on a `[data-apply-cta]` link → adds `is-loading` class, sets
 *     `aria-busy="true"`, and disables further pointer events on the
 *     element. The native navigation continues — we don't preventDefault.
 *   - Disabled state is cleared by `pageshow` so a back-button restore
 *     from bfcache doesn't leave the spinner running on a stale page.
 *   - Modifier-clicks (cmd/ctrl/shift/middle-button) skip the loader so
 *     "open in new tab" doesn't lock the visible button on the source page.
 */

const SELECTOR = '[data-apply-cta]';
const LOADING_CLASS = 'is-loading';

function isModifiedClick(event) {
	return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0;
}

function setLoading(el) {
	if (!el || el.classList.contains(LOADING_CLASS)) return;
	el.classList.add(LOADING_CLASS);
	el.setAttribute('aria-busy', 'true');
	// Block double-clicks from sending a second navigation. We don't use
	// `disabled` (the element is an anchor, not a button — `disabled` on
	// an `<a>` has no effect) — instead, intercept further clicks and
	// noop them until pageshow fires.
	el.style.pointerEvents = 'none';
}

function clearLoading(el) {
	if (!el) return;
	el.classList.remove(LOADING_CLASS);
	el.removeAttribute('aria-busy');
	el.style.pointerEvents = '';
}

export const ApplyLoader = {
	init() {
		const links = document.querySelectorAll(SELECTOR);
		if (!links.length) return;

		links.forEach((link) => {
			link.addEventListener('click', (event) => {
				if (isModifiedClick(event)) return;
				if (link.classList.contains(LOADING_CLASS)) {
					event.preventDefault();
					return;
				}
				setLoading(link);
			});
		});

		// Restore from bfcache (Safari / Firefox back-button) — without this
		// the user lands back on the source page with the button locked
		// in its loading state.
		window.addEventListener('pageshow', (event) => {
			if (event.persisted) {
				links.forEach(clearLoading);
			}
		});
	},
};
